import { t } from 'i18next';
import { useMutation } from 'react-query';

import { logService } from 'bom/services';
import { ILog, ILogListRequestPayload } from 'bom/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { IInfiniteQueryOptions } from 'core/types';

export const useGetLogs = (
  requestPayload?: ILogListRequestPayload,
  options: IInfiniteQueryOptions<ILog> = {}
) => useInfiniteQuery<ILog>(logService.getLogs, 'logs', requestPayload, options);

export const useLogActions = ({
  notificationPosition,
}: { notificationPosition?: NotificationPosition } = {}) => ({
  ...useUpsertLog(),
  ...useUpsertLogs(),
  ...useRemoveLogs(notificationPosition),
});

const useUpsertLog = () => {
  const { errors } = useHandleErrors({
    success: ({ bomLog }) =>
      bomLog.id
        ? { message: t('messageLogUpdateSuccess') }
        : { message: t('messageLogCreateSuccess') },
    loading: ({ bomLog }) =>
      bomLog.id
        ? { message: t('messageLogUpdateInProgress') }
        : { message: t('messageLogCreateInProgress') },
    error: ({ bomLog }) =>
      bomLog?.id ? { title: t('errorTitleLogUpdate') } : { title: t('errorTitleLogCreate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(logService.upsertLog, {
    ...errors,
  });

  return { upsertLog: mutateAsync };
};

const useUpsertLogs = () => {
  const { errors } = useHandleErrors({
    success: ({ bomLogs }) => ({
      message: bomLogs?.[0]?.id
        ? t('messageLogsUpdateSuccess', { count: bomLogs.length })
        : t('messageLogsCreateSuccess', { count: bomLogs.length }),
    }),
    loading: ({ bomLogs }) => ({
      message: bomLogs?.[0]?.id
        ? t('messageLogsUpdateInProgress', { count: bomLogs.length })
        : t('messageLogsCreateInProgress', { count: bomLogs.length }),
    }),
    error: ({ bomLogs }) => ({
      title: bomLogs?.[0]?.id
        ? t('errorTitleLogsUpdate', { count: bomLogs.length })
        : t('errorTitleLogsCreate', { count: bomLogs.length }),
    }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(logService.upsertLogs, {
    ...errors,
  });

  return { upsertLogs: mutateAsync };
};

const useRemoveLogs = (notificationPosition = NotificationPosition.FORM) => {
  const { errors } = useHandleErrors({
    success: ({ ids }) => ({
      message: t('messageLogsRemoveSuccess', { count: ids.length }),
    }),
    loading: ({ ids }) => ({
      message: t('messageLogsRemoveInProgress', { count: ids.length }),
    }),
    error: ({ ids }) => ({
      title: t('errorTitleLogsRemove', { count: ids.length }),
    }),
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(logService.removeLogs, {
    ...errors,
  });

  return { removeLogs: mutateAsync };
};
